import { ReactNode, useMemo } from 'react';

import {
  Locale,
  format,
  getDate,
  getDay,
  setHours,
  setMinutes,
  startOfToday,
} from 'date-fns';

import { useLocale } from '@adsk/alloy-react-locale';

import {
  DATE_FNS_LOCALE,
  DAYS_OF_THE_WEEK_IN_ENGLISH,
  INTERVAL_UNITS,
} from '../helpers/consts';
import { IntervalUnit, RunOn } from '../types';

const timeInterval = (interval: Date | number, localeObject: Locale) => ({
  label: format(interval, 'p', { locale: localeObject }),
  value: format(interval, 'p'),
});

const timeOfDayEvery30Minutes = (date: Date | number, localeObject: Locale) => {
  const hours: ReturnType<typeof timeInterval>[] = [];
  Array.from({ length: 24 }, (_, hour) => {
    const hoursInterval = setHours(date, hour);
    const minutesInterval = setMinutes(hoursInterval, 30);
    hours.push(timeInterval(hoursInterval, localeObject));
    hours.push(timeInterval(minutesInterval, localeObject));
  });

  return hours;
};

const formatUnits = (
  units: IntervalUnit[],
  T: { translate: (key: string) => ReactNode },
) => {
  return units.map((unit) => ({
    label: T.translate(`${unit}.LABEL`) as string,
    value: unit,
  }));
};

const useScheduleData = ({ units }: { units: IntervalUnit[] }) => {
  const { T, locale } = useLocale(({ locale: loc }) => {
    return require(`../i18n/${loc}.json`);
  });

  const localeObject = useMemo(() => {
    return (
      DATE_FNS_LOCALE[locale as keyof typeof DATE_FNS_LOCALE] ||
      DATE_FNS_LOCALE.en
    );
  }, [locale]);

  // Dates
  const todayDate = startOfToday();
  const todayDateNumber = getDate(todayDate);
  const todayWeekDay = getDay(todayDate);
  const startOfWeek = localeObject.options?.weekStartsOn;

  const defaultRunOn: Record<IntervalUnit, RunOn[]> = {
    [INTERVAL_UNITS.DAY]: [null],
    [INTERVAL_UNITS.WEEK]: [DAYS_OF_THE_WEEK_IN_ENGLISH[todayWeekDay]],
    [INTERVAL_UNITS.MONTH]: [todayDateNumber],
  };

  // Dropdown data
  const runAtTimes = useMemo(
    () => timeOfDayEvery30Minutes(todayDate, localeObject),
    [localeObject, todayDate],
  );
  const formattedIntervalUnits = formatUnits(units, T);

  return {
    localeObject,
    defaultRunOn,
    todayDate,
    todayDateNumber,
    todayWeekDay: todayWeekDay as 0 | 1 | 2 | 3 | 4 | 5 | 6,
    startOfWeek,
    runAtTimes,
    formattedIntervalUnits,
  };
};

export default useScheduleData;
